import * as React from 'react'
import {
    Box,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import RestaurantRoundedIcon from '@mui/icons-material/RestaurantRounded'

import useAutoTranslate from '../hooks/useAutoTranslate'

const PLUGIN_KEY = 'feast'
const DEFAULT_INTERVAL_MINUTES = '10'
const DEFAULT_MINIMUM_FOOD = '400000'

const SECTION_DEFINITIONS = Object.freeze([
    {
        id: 'main',
        configKey: 'section:main',
        title: 'Green',
        prefix: 'Main',
        defaultEnabled: true,
        enableHelper: 'Enable automatic feast in main kingdom',
        minimumFoodHelper: 'Minimum food kept in main kingdom before feasting'
    },
    {
        id: 'ice',
        configKey: 'section:ice',
        title: 'Ice',
        prefix: 'Ice',
        defaultEnabled: true,
        enableHelper: 'Enable automatic feast in ice kingdom',
        minimumFoodHelper: 'Minimum food kept in ice kingdom before feasting'
    },
    {
        id: 'desert',
        configKey: 'section:desert',
        title: 'Desert',
        prefix: 'Desert',
        defaultEnabled: true,
        enableHelper: 'Enable automatic feast in desert kingdom',
        minimumFoodHelper: 'Minimum food kept in desert kingdom before feasting'
    },
    {
        id: 'fire',
        configKey: 'section:fire',
        title: 'Fire',
        prefix: 'Fire',
        defaultEnabled: true,
        enableHelper: 'Enable automatic feast in fire kingdom',
        minimumFoodHelper: 'Minimum food kept in fire kingdom before feasting'
    },
    {
        id: 'capital',
        configKey: 'section:capital',
        title: 'Capital',
        prefix: 'Capital',
        defaultEnabled: false,
        enableHelper: 'Enable automatic feast in capital (KID 0/1/2/3)',
        minimumFoodHelper: 'Minimum food kept in capital before feasting'
    },
    {
        id: 'metro',
        configKey: 'section:metro',
        title: 'Metro',
        prefix: 'Metro',
        defaultEnabled: false,
        enableHelper: 'Enable automatic feast in metro (KID 0)',
        minimumFoodHelper: 'Minimum food kept in metro before feasting'
    }
])

const cloneValue = value =>
    typeof structuredClone === 'function' ?
        structuredClone(value) :
        JSON.parse(JSON.stringify(value ?? {}))

const normalizePositiveNumberText = (value, fallback = '') => {
    if ([undefined, null, ''].includes(value))
        return fallback

    const parsed = Number.parseInt(String(value), 10)
    return Number.isFinite(parsed) && parsed >= 0 ? String(parsed) : fallback
}

const normalizeSectionConfig = (value, defaults) => ({
    enabled: typeof value?.enabled === 'boolean' ? value.enabled : defaults.defaultEnabled,
    minimumFood: normalizePositiveNumberText(value?.minimumFood, DEFAULT_MINIMUM_FOOD)
})

const FIELD_INPUT_SX = {
    '& .MuiOutlinedInput-root': {
        minHeight: 42,
        borderRadius: '10px',
        backgroundColor: 'rgba(24, 36, 57, 0.76)'
    },
    '& .MuiInputBase-input': {
        fontSize: '1.01rem',
        fontWeight: 700,
        color: '#f5f7ff'
    }
}

function Helper({ children }) {
    return (
        <Stack direction="row" spacing={0.7} alignItems="center" sx={{ mt: 0.8 }}>
            <InfoOutlinedIcon sx={{ fontSize: 13, color: '#95a5c4' }} />
            <Typography sx={{ fontSize: '0.89rem', color: '#95a5c4', lineHeight: 1.35 }}>{children}</Typography>
        </Stack>
    )
}

function FieldTitle({ children }) {
    return (
        <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>
            {children}
        </Typography>
    )
}

function ToggleField({ checked, onChange, label, helper }) {
    return (
        <Box sx={{ minHeight: 54 }}>
            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ pt: 1.15 }}>
                <Switch
                    checked={checked}
                    onChange={event => onChange(event.target.checked)}
                    sx={{
                        width: 48,
                        height: 29,
                        p: 0,
                        '& .MuiSwitch-switchBase': {
                            p: '2.5px',
                            '&.Mui-checked': {
                                transform: 'translateX(18px)',
                                color: '#ffffff'
                            },
                            '&.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#2f7cff',
                                opacity: 1
                            }
                        },
                        '& .MuiSwitch-thumb': {
                            width: 24,
                            height: 24,
                            boxShadow: 'none'
                        },
                        '& .MuiSwitch-track': {
                            opacity: 1,
                            borderRadius: '999px',
                            backgroundColor: 'rgba(111, 123, 143, 0.9)'
                        }
                    }}
                />
                <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800 }}>
                    {label}
                </Typography>
            </Stack>
            <Helper>{helper}</Helper>
        </Box>
    )
}

function SectionHeading({ children }) {
    return (
        <Stack direction="row" alignItems="center" spacing={1.3} sx={{ mb: 2.2 }}>
            <Typography sx={{ color: '#f5f7ff', fontSize: '1.05rem', fontWeight: 800 }}>{children}</Typography>
            <Box sx={{ flex: 1, height: 1, backgroundColor: 'rgba(129, 146, 178, 0.22)' }} />
        </Stack>
    )
}

export default function AutomaticFeastSettingsPanel({ userPlugins, onChange, appLanguage = 'en' }) {
    const pluginState = React.useMemo(
        () => userPlugins?.[PLUGIN_KEY] && typeof userPlugins[PLUGIN_KEY] === 'object' ? userPlugins[PLUGIN_KEY] : {},
        [userPlugins]
    )
    const locationConfigs = React.useMemo(
        () => pluginState?.locationConfigs && typeof pluginState.locationConfigs === 'object' ? pluginState.locationConfigs : {},
        [pluginState]
    )

    const t = useAutoTranslate(appLanguage, [
        'Automatic Feast Settings',
        'Configure automatic feast management for all kingdoms',
        'Check Interval (Minutes)',
        'How often to check feast status in minutes',
        'Enable Feast',
        'Minimum Food',
        ...SECTION_DEFINITIONS.map(section => section.title),
        ...SECTION_DEFINITIONS.flatMap(section => [
            section.enableHelper,
            section.minimumFoodHelper
        ])
    ])

    const config = React.useMemo(() => ({
        intervalMinutes: normalizePositiveNumberText(pluginState?.intervalMinutes, DEFAULT_INTERVAL_MINUTES),
        sections: Object.fromEntries(
            SECTION_DEFINITIONS.map(section => [
                section.id,
                normalizeSectionConfig(locationConfigs[section.configKey], section)
            ])
        )
    }), [locationConfigs, pluginState?.intervalMinutes])

    const commit = React.useCallback(nextConfig => {
        const nextPlugins = cloneValue(userPlugins ?? {})
        nextPlugins[PLUGIN_KEY] ??= {}
        nextPlugins[PLUGIN_KEY].intervalMinutes = normalizePositiveNumberText(nextConfig.intervalMinutes, DEFAULT_INTERVAL_MINUTES)
        nextPlugins[PLUGIN_KEY].locationConfigs = Object.fromEntries(
            SECTION_DEFINITIONS.map(section => [
                section.configKey,
                {
                    enabled: Boolean(nextConfig.sections[section.id]?.enabled),
                    minimumFood: normalizePositiveNumberText(nextConfig.sections[section.id]?.minimumFood, DEFAULT_MINIMUM_FOOD)
                }
            ])
        )
        onChange(nextPlugins)
    }, [onChange, userPlugins])

    const update = updater => {
        const nextConfig = cloneValue(config)
        updater(nextConfig)
        commit(nextConfig)
    }

    return (
        <Paper
            sx={{
                p: { xs: 2.2, md: 3.1 },
                borderRadius: '22px',
                border: '1px solid rgba(129, 146, 178, 0.18)',
                background: 'linear-gradient(180deg, rgba(27, 40, 63, 0.9) 0%, rgba(24, 35, 56, 0.86) 100%)',
                boxShadow: '0 22px 46px rgba(7, 13, 26, 0.22)'
            }}
        >
            <Stack spacing={3.2}>
                <Box>
                    <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 0.55 }}>
                        <RestaurantRoundedIcon sx={{ color: '#f5f7ff', fontSize: 22 }} />
                        <Typography sx={{ color: '#f5f7ff', fontSize: '1.42rem', fontWeight: 800 }}>
                            {t('Automatic Feast Settings')}
                        </Typography>
                    </Stack>
                    <Typography sx={{ color: '#9fb0cc', fontSize: '1.02rem' }}>
                        {t('Configure automatic feast management for all kingdoms')}
                    </Typography>
                </Box>

                <Box>
                    <FieldTitle>{t('Check Interval (Minutes)')}</FieldTitle>
                    <TextField
                        fullWidth
                        value={config.intervalMinutes}
                        onChange={event => update(next => { next.intervalMinutes = event.target.value })}
                        sx={FIELD_INPUT_SX}
                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    />
                    <Helper>{t('How often to check feast status in minutes')}</Helper>
                </Box>

                {SECTION_DEFINITIONS.map(section => {
                    const sectionConfig = config.sections[section.id]

                    return (
                        <Box key={section.id}>
                            <SectionHeading>{t(section.title)}</SectionHeading>

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }, gap: 2.8 }}>
                                <Stack spacing={2.2}>
                                    <ToggleField
                                        checked={sectionConfig.enabled}
                                        onChange={value => update(next => { next.sections[section.id].enabled = value })}
                                        label={`${section.prefix}: ${t('Enable Feast')}`}
                                        helper={t(section.enableHelper)}
                                    />
                                </Stack>

                                <Stack spacing={2.2}>
                                    <Box>
                                        <FieldTitle>{`${section.prefix}: ${t('Minimum Food')}`}</FieldTitle>
                                        <TextField
                                            fullWidth
                                            value={sectionConfig.minimumFood}
                                            onChange={event => update(next => { next.sections[section.id].minimumFood = event.target.value })}
                                            sx={FIELD_INPUT_SX}
                                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                        />
                                        <Helper>{t(section.minimumFoodHelper)}</Helper>
                                    </Box>
                                </Stack>
                            </Box>
                        </Box>
                    )
                })}
            </Stack>
        </Paper>
    )
}
